import { forwardRef, useLayoutEffect, useRef } from "react";

import mergeRefs from "merge-refs";

type ChatMessageListPagination = {
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  loadingSkeleton: React.ReactNode;
};

type ChatMessageListProps = {
  children: React.ReactNode;
  dataLoc?: string;
  pagination?: ChatMessageListPagination;
};

export const ChatMessageList = forwardRef<
  HTMLUListElement,
  ChatMessageListProps
>(({ children, dataLoc, pagination }, ref) => {
  const internalRef = useRef<HTMLUListElement>(null);
  const prevScrollDataRef = useRef<{ height: number; top: number } | null>(
    null,
  );

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    if (!pagination) return;
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = pagination;
    const list = internalRef.current;

    if (!list) return;

    if (isFetchingNextPage && prevScrollDataRef.current?.top !== undefined) {
      // Keep tracking the scroll top when fetching next page
      // User can keep scrolling up while the next page is fetching
      prevScrollDataRef.current.top = e.currentTarget.scrollTop;
    }

    if (list.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      prevScrollDataRef.current = {
        height: list.scrollHeight,
        top: list.scrollTop,
      };

      fetchNextPage();
    }
  };

  useLayoutEffect(() => {
    if (!pagination) return;

    const list = internalRef.current;
    const scrollData = prevScrollDataRef.current;

    if (!list || !scrollData) return;

    if (
      !pagination.isFetchingNextPage &&
      list.scrollHeight !== scrollData.height
    ) {
      list.scrollTop = scrollData.top + (list.scrollHeight - scrollData.height);

      prevScrollDataRef.current = null;
    }
  }, [children, pagination?.isFetchingNextPage]);

  return (
    <ul
      ref={mergeRefs(internalRef, ref)}
      className="decideScrollbar flex h-full flex-col gap-y-2 p-2"
      data-loc={dataLoc}
      onScroll={handleScroll}
    >
      {pagination?.hasNextPage && pagination.loadingSkeleton}
      {children}
    </ul>
  );
});
